import { Space } from "@classdojo/web/nessie";
import { RAW_cssValue } from "@classdojo/web/nessie/stylingLib";
import { translate } from "@classdojo/web/utils/translate";
import { ReactNode, useCallback } from "react";
import FullWidthButton from "./FullWidthButton";
import { IdeasChevronLeftIcon, IdeasPauseIcon, IdeasPlayIcon } from "./IdeasIcons";
import RoundedIconButton from "./RoundedIconButton";
import { playStates } from "hooks/useAudioPlayer";
import { closeWebview } from "src/pods/webviewInit";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { formatTime } from "utils";

type ToolkitAudioActivityChromeProps = {
  play: VoidFunction;
  pause: VoidFunction;
  playState: string;
  secondsRemaining: number;
  mainContent: ReactNode;
  closeAndGivePoints: VoidFunction;
  loggingId: string;
};

/**
 * Presentation component that renders standard visuals for an audio activity such as
 * time remaining, and action buttons.
 *
 * Actual audio playing and state management should be handled by the parent
 */
export default function ToolkitAudioActivityChrome({
  play,
  pause,
  playState,
  secondsRemaining,
  mainContent,
  closeAndGivePoints,
  loggingId,
}: ToolkitAudioActivityChromeProps) {
  const closeActivityAndLogEvent = useCallback(
    (logAction: string) => {
      logParentToolkitEvent({ eventName: `${loggingId}.${logAction}` });
      // for iOS native toolkit landing page
      // handling on the iOS side for this action does not exist on older clients
      // so shouldn't affect toolkit with the webview landing page
      closeWebview();
      window.history.back();
    },
    [loggingId],
  );
  return (
    <div
      sx={{
        textAlign: "center",
        height: RAW_cssValue("calc(100svh - env(safe-area-inset-top) - env(safe-area-inset-bottom))"),
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <div sx={{ position: "relative" }}>
        {/*Back arrow*/}
        <div
          sx={{ position: "absolute", top: "dt_m", left: "dt_m", zIndex: 10 }}
          onClick={() => {
            closeActivityAndLogEvent("close.tap");
          }}
        >
          <IdeasChevronLeftIcon color="dt_content_light" />
        </div>
      </div>

      <Space size="xl" />

      {/*Title Text*/}
      <div
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <div sx={{ color: "dt_content_light", fontWeight: 800, fontSize: 42 }}>
          {titleText(playState, secondsRemaining)}
        </div>
      </div>

      {/*Image*/}
      <div
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "dt_l",
          justifyContent: "center",
          flexGrow: 1,
          position: "relative",
        }}
      >
        {mainContent}
      </div>

      {/*Button(s)*/}
      {playState == playStates.READY && (
        <div
          sx={{
            display: "flex",
            textAlign: "center",
            padding: "dt_l",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <FullWidthButton
            data-name="toolkit_audio_activity_chrome:brush_teeth:go"
            text={translate("dojo.beyond:toolkit.brush_teeth.go")}
            onClick={() => {
              logParentToolkitEvent({ eventName: `${loggingId}.start` });
              play();
            }}
            bgColor={"dt_background_primary"}
            textColor={"dt_content_accent"}
          />
        </div>
      )}

      {(playState == playStates.PLAYING || playState == playStates.PAUSED) && (
        <div
          sx={{
            display: "flex",
            textAlign: "center",
            padding: "dt_l",
            justifyContent: "center",
            position: "relative",
          }}
        >
          {playState === playStates.PLAYING ? (
            <RoundedIconButton
              backgroundColor="dt_background_primary"
              onClick={() => pause()}
              data-name="toolkit_audio_activity_chrome:pause_button"
            >
              <IdeasPauseIcon size="l" color="dt_content_accent" />
            </RoundedIconButton>
          ) : (
            <RoundedIconButton
              backgroundColor="dt_background_primary"
              onClick={() => play()}
              data-name="toolkit_audio_activity_chrome:play_button"
            >
              <IdeasPlayIcon size="l" color="dt_content_accent" />
            </RoundedIconButton>
          )}
        </div>
      )}

      {playState == playStates.DONE && (
        <div
          sx={{
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
            padding: "dt_l",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <FullWidthButton
            data-name="toolkit_audio_activity_chrome:brush_teeth:give_points"
            text={translate("dojo.beyond:toolkit.brush_teeth.give_points")}
            onClick={() => {
              logParentToolkitEvent({
                eventName: `${loggingId}.give_points.tap`,
              });
              closeAndGivePoints && closeAndGivePoints();
            }}
            bgColor={`dt_background_primary`}
            textColor={"dt_content_accent"}
          />
          <Space size="m" />
          <div
            onClick={() => {
              closeActivityAndLogEvent("done.tap");
            }}
            sx={{ color: "dt_content_light", fontWeight: 800, fontSize: 18 }}
          >
            {translate("dojo.beyond:toolkit.whose_turn.done")}
          </div>
        </div>
      )}
    </div>
  );
}

function titleText(
  playState: ToolkitAudioActivityChromeProps["playState"],
  secondsRemaining: ToolkitAudioActivityChromeProps["secondsRemaining"],
) {
  switch (playState) {
    case playStates.READY:
      return translate("dojo.beyond:toolkit.brush_teeth.ask_ready");
    case playStates.DONE:
      return translate("dojo.beyond:toolkit.brush_teeth.good_job");
    default:
      return formatTime(secondsRemaining);
  }
}
